import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../services/api';
import * as fmt from '../lib/format';

interface LogTypeBreakdownItem {
  log_type: string;
  minutes: number;
  hours: number;
}

interface LogTypePopoverProps {
  accountId: string;
  date?: string;
  year?: number;
  month?: number;
  onLogTypeClick: (logType: string) => void;
  children: React.ReactNode;
  designationTypes?: string[];
}

// Colour scheme for tag pills
export const LOG_TYPE_COLOURS: Record<string, string> = {
  'Dev':              'bg-blue-100 text-blue-700',
  'Debug':            'bg-red-100 text-red-700',
  'Code Review':      'bg-indigo-100 text-indigo-700',
  'Discussion':       'bg-sky-100 text-sky-700',
  'Research':         'bg-amber-100 text-amber-700',
  'DevOps':           'bg-violet-100 text-violet-700',
  'Hotfix':           'bg-orange-100 text-orange-700',
  'Documentation':    'bg-teal-100 text-teal-700',
  'Rework':           'bg-rose-100 text-rose-700',
  'Testing':          'bg-emerald-100 text-emerald-700',
  'Bug Verification': 'bg-lime-100 text-lime-700',
  'Test Setup':       'bg-cyan-100 text-cyan-700',
  'Scoping':          'bg-purple-100 text-purple-700',
  'Review':           'bg-fuchsia-100 text-fuchsia-700',
  'Coordination':     'bg-green-100 text-green-700',
  'Design':           'bg-pink-100 text-pink-700',
  'Revision':         'bg-yellow-100 text-yellow-700',
  'Automation':       'bg-slate-100 text-slate-700',
  '[]':               'bg-gray-100 text-gray-600',
};

// The colour of a worklog tag's pill; a tag with no colour of its own is grey.
export function tagColour(logType: string): string {
  return LOG_TYPE_COLOURS[logType] ?? 'bg-gray-100 text-gray-600';
}

const LogTypePopover: React.FC<LogTypePopoverProps> = ({ accountId, date, year, month, onLogTypeClick, children, designationTypes }) => {
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState<LogTypeBreakdownItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Cache: keyed by "accountId|date" so each unique cell fetches once
  const cache = useRef<Record<string, LogTypeBreakdownItem[]>>({});

  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  // Popover position anchored below the trigger cell, flipped above if it overflows viewport bottom
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4 + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, []);

  // After popover renders, flip above trigger if it overflows viewport bottom or right
  useEffect(() => {
    if (!visible || !popoverRef.current || !triggerRef.current) return;
    const pop = popoverRef.current.getBoundingClientRect();
    const trig = triggerRef.current.getBoundingClientRect();
    let top = trig.bottom + 4 + window.scrollY;
    let left = trig.left + window.scrollX;
    if (pop.bottom > window.innerHeight - 8) {
      top = trig.top + window.scrollY - pop.height - 4;
    }
    if (pop.right > window.innerWidth - 8) {
      left = window.innerWidth - pop.width - 8 + window.scrollX;
    }
    setPos({ top, left });
  }, [visible, items, loading]);

  const fetchData = useCallback(async () => {
    const key = year != null && month != null ? `${accountId}|${year}|${month}` : `${accountId}|${date}`;
    if (cache.current[key]) {
      setItems(cache.current[key]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    try {
      const res = year != null && month != null
        ? await apiService.getUserMonthLogType(accountId, year, month)
        : await apiService.getUserDayLogType(accountId, date as string);
      if (!cancelled && res?.success && Array.isArray(res.data)) {
        let result: LogTypeBreakdownItem[] = res.data;
        if (designationTypes && designationTypes.length > 0) {
          const actualMap = new Map<string, LogTypeBreakdownItem>(res.data.map((i: LogTypeBreakdownItem) => [i.log_type, i]));
          const merged = designationTypes.map(lt => actualMap.get(lt) ?? { log_type: lt, minutes: 0, hours: 0 });
          // Append any actual log types not in the designation list (e.g. new tags like Automation).
          const known = new Set(designationTypes);
          for (const it of res.data as LogTypeBreakdownItem[]) {
            if (it.log_type !== '[]' && !known.has(it.log_type)) merged.push(it);
          }
          if (actualMap.has('[]')) merged.push(actualMap.get('[]')!);
          result = merged;
        }
        cache.current[key] = result;
        setItems(result);
      }
    } catch {
      // silently skip — popover stays empty
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [accountId, date, year, month, designationTypes]);

  const handleMouseEnter = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    computePos();
    setVisible(true);
    fetchData();
  }, [computePos, fetchData]);

  const handleMouseLeave = useCallback(() => {
    hideTimer.current = setTimeout(() => setVisible(false), 150);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  const handlePopoverLeave = useCallback(() => {
    hideTimer.current = setTimeout(() => setVisible(false), 150);
  }, []);

  const handleClick = useCallback((logType: string) => {
    setVisible(false);
    onLogTypeClick(logType);
  }, [onLogTypeClick]);

  return (
    <>
      <div
        ref={triggerRef}
        role="presentation"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        // Tabbing to the number shows the same breakdown as pointing at it; Escape puts it away.
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setVisible(false);
        }}
        className="w-full"
      >
        {children}
      </div>

      {visible && (loading || items.length > 0) && createPortal(
        <div
          ref={popoverRef}
          role="presentation"
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 1000020 }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl py-1.5 min-w-[160px]"
        >
          {loading ? (
            <div className="px-3 py-2">
              <div className="h-3 bg-gray-100 rounded animate-pulse w-24 mb-1.5" />
              <div className="h-3 bg-gray-100 rounded animate-pulse w-16" />
            </div>
          ) : items.length === 0 ? null : (
            items.map(item => (
              <button
                key={item.log_type}
                type="button"
                onClick={() => handleClick(item.log_type)}
                className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 text-left gap-2"
              >
                <span className={`text-[0.6875rem] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${tagColour(item.log_type)}`}>
                  {item.log_type === '[]' ? '[]' : `[${item.log_type}]`}
                </span>
                <span className="text-xs text-gray-500">-</span>
                <span className="text-xs font-bold text-gray-700">{fmt.hours(item.hours)}</span>
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </>
  );
};

export default LogTypePopover;
